import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Select } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { relativeTime } from '@/lib/format';
import { listCompanies } from '@/data/client';
import { useMembers } from '@/data/hooks';
import type { Lifecycle } from '@/data/types';
import { useSearch } from '@/components/shell/search';
import { LifecycleBadge, LIFECYCLE_OPTIONS } from '@/components/crm/LifecycleBadge';
import { CompanyFormModal } from '@/components/crm/CompanyForm';
import { ImportCompaniesModal } from '@/components/crm/ImportCompanies';
import { useMe } from '@/auth/useMe';

export function Companies() {
  const navigate = useNavigate();
  const { query } = useSearch();
  const { ownerOptions } = useMembers();
  const [lifecycle, setLifecycle] = useState<Lifecycle | ''>('');
  // Past leads (lost, never a client, nothing open) are most of an imported history: hidden unless asked for.
  const [view, setView] = useState<'active' | 'past_leads' | ''>('active');
  const [owner, setOwner] = useState('');
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const { me } = useMe();
  const { data, isLoading } = useQuery({
    queryKey: ['companies', { q: query, lifecycle, owner, view }],
    queryFn: () => listCompanies({ q: query, lifecycle, owner, view }),
    placeholderData: (prev) => prev,
  });

  return (
    <div className="crm-list">
      <div className="lead-toolbar">
        <div className="lead-toolbar__filters">
          <label className="lead-field">
            <Icon name="filter" size={16} />
            <Select aria-label="Lifecycle" options={[{ value: '', label: 'All stages' }, ...LIFECYCLE_OPTIONS]} value={lifecycle} onChange={(e) => setLifecycle(e.target.value as Lifecycle | '')} />
          </label>
          <label className="lead-field">
            <Icon name="layers" size={16} />
            <Select aria-label="Show" value={view} onChange={(e) => setView(e.target.value as typeof view)}
              options={[{ value: 'active', label: 'Hide past leads' }, { value: 'past_leads', label: 'Past leads only' }, { value: '', label: 'Everything' }]} />
          </label>
          <label className="lead-field">
            <Icon name="users" size={16} />
            <Select aria-label="Owner" options={[{ value: '', label: 'All owners' }, ...ownerOptions]} value={owner} onChange={(e) => setOwner(e.target.value)} />
          </label>
        </div>
        <div className="lead-toolbar__right">
          {me?.isAdmin && <Button variant="outline" iconLeft={<Icon name="upload" size={16} />} onClick={() => setImporting(true)}>Import</Button>}
          <Button iconLeft={<Icon name="plus" size={16} />} onClick={() => setCreating(true)}>New company</Button>
        </div>
      </div>

      <Card padding={false}>
        <table className="dash-table lead-table">
          <thead>
            <tr><th>Company</th><th>Lifecycle</th><th>Owner</th><th className="crm-num">Contacts</th><th className="crm-num">Open deals</th><th>Last activity</th></tr>
          </thead>
          <tbody>
            {data?.map((c) => (
              <tr key={c.id} onClick={() => navigate(`/companies/${c.id}`)}>
                <td><strong>{c.name}</strong>{c.domain && <span className="crm-cell-sub">{c.domain}</span>}</td>
                <td><LifecycleBadge lifecycle={c.lifecycle} /></td>
                <td className={c.ownerName ? '' : 'crm-cell-muted'}>{c.ownerName ?? 'Unassigned'}</td>
                <td className="crm-num">{c.contactCount}</td>
                <td className="crm-num">{c.openDeals}</td>
                <td className="crm-cell-muted">{relativeTime(c.lastActivityAt ?? c.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && !data?.length && (
          <p className="lead-none">{query || lifecycle || owner || view === 'past_leads' ? 'No companies match these filters.' : 'No companies yet. Add one, or they will appear as leads arrive.'}</p>
        )}
      </Card>

      {creating && <CompanyFormModal onClose={() => setCreating(false)} />}
      {importing && <ImportCompaniesModal onClose={() => setImporting(false)} />}
    </div>
  );
}
